import { useQuery } from '@tanstack/react-query';
import type {
  CommentDto,
  FileDto,
  RequestDto,
  ServiceCategoryDto,
  TaskDto,
  TaskSummaryDto,
  WorkflowDto,
} from '@cw/shared';
import { api } from './api';
import { useCan } from './session';

export const taskKeys = {
  all: ['tasks'] as const,
  list: (qs: string) => ['tasks', 'list', qs] as const,
  detail: (id: string) => ['tasks', 'detail', id] as const,
  files: (id: string) => ['tasks', 'detail', id, 'files'] as const,
  workflow: (projectId: string | null) => ['workflow', projectId ?? 'default'] as const,
  services: ['services'] as const,
  comments: (type: string, id: string) => ['comments', type, id] as const,
  requests: (status: string) => ['requests', status] as const,
  request: (id: string) => ['requests', 'detail', id] as const,
};

export type TaskFilters = {
  projectId?: string;
  clientId?: string;
  parentId?: string;
  assignee?: string;
  due?: 'overdue' | 'today' | 'week' | 'none';
  open?: boolean;
  q?: string;
};

export function taskQs(f: TaskFilters): string {
  const p = new URLSearchParams();
  for (const [k, v] of Object.entries(f))
    if (v !== undefined && v !== false && v !== '') p.set(k, v === true ? '1' : String(v));
  return p.toString();
}

export function useTasks(f: TaskFilters, enabled = true) {
  const qs = taskQs(f);
  return useQuery({
    queryKey: taskKeys.list(qs),
    queryFn: () => api.get<TaskSummaryDto[]>(`/tasks?${qs}`),
    enabled: useCan('tasks:read') && enabled,
  });
}

export function useTask(id: string | null) {
  return useQuery({
    queryKey: taskKeys.detail(id ?? ''),
    queryFn: () => api.get<TaskDto>(`/tasks/${id}`),
    enabled: Boolean(id),
  });
}

export function useWorkflow(projectId: string | null) {
  return useQuery({
    queryKey: taskKeys.workflow(projectId),
    queryFn: () => api.get<WorkflowDto>(projectId ? `/workflows/project/${projectId}` : '/workflows/default'),
    staleTime: 60_000,
    enabled: useCan('tasks:read'),
  });
}

export function useServices() {
  return useQuery({
    queryKey: taskKeys.services,
    queryFn: () => api.get<ServiceCategoryDto[]>('/services'),
    staleTime: 60_000,
  });
}

export function useComments(entityType: string, entityId: string) {
  return useQuery({
    queryKey: taskKeys.comments(entityType, entityId),
    queryFn: () => api.get<CommentDto[]>(`/comments/${entityType}/${entityId}`),
  });
}

export function useTaskFiles(id: string) {
  return useQuery({
    queryKey: taskKeys.files(id),
    queryFn: () => api.get<FileDto[]>(`/files?taskId=${id}&purpose=task_attachment`),
  });
}

export function useRequests(status: string) {
  return useQuery({
    queryKey: taskKeys.requests(status),
    queryFn: () => api.get<RequestDto[]>(`/requests${status === 'all' ? '' : `?status=${status}`}`),
    enabled: useCan('requests:create'),
  });
}

/** Prazos de tarefa: data (YYYY-MM-DD do input) → fim do expediente local (18h) em ISO. */
export function dueFromDateInput(v: string): string | null {
  if (!v) return null;
  const [y, m, d] = v.split('-').map(Number);
  return new Date(y!, m! - 1, d!, 18, 0, 0).toISOString();
}

export function dateInputFrom(iso: string | null): string {
  if (!iso) return '';
  const d = new Date(iso);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

/** Dias até o prazo (calendário local). */
export function daysUntilIso(iso: string | null): number | null {
  if (!iso) return null;
  const due = new Date(iso);
  const a = new Date(due.getFullYear(), due.getMonth(), due.getDate());
  const now = new Date();
  const b = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  return Math.round((a.getTime() - b.getTime()) / 86_400_000);
}
